import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    RefreshControl,
    ActivityIndicator,
    Alert,
    StatusBar,
    Platform,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import axios from 'axios';
import { COLORS, API, APP_INFO } from '../config';
import { useAuth } from '../context/AuthContext';

export default function VoucherScreen() {
    const { user, refreshUser } = useAuth();
    const [vouchers, setVouchers] = useState([]);
    const [myVouchers, setMyVouchers] = useState([]);
    const [myUndian, setMyUndian] = useState([]);
    const [activeTab, setActiveTab] = useState('available');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchData = async () => {
        try {
            const [vouchersRes, myVouchersRes, undianRes] = await Promise.all([
                axios.get(API.VOUCHER.LIST).catch(() => ({ data: { success: true, data: [] } })),
                axios.get(API.VOUCHER.MY_VOUCHERS(user.id)).catch(() => ({ data: { success: true, data: [] } })),
                axios.get(API.UNDIAN.LIST(user.id)).catch(() => ({ data: { success: true, data: [] } })),
            ]);

            if (vouchersRes.data.success) setVouchers(vouchersRes.data.data || []);
            if (myVouchersRes.data.success) setMyVouchers(myVouchersRes.data.data || []);
            if (undianRes.data.success) setMyUndian(undianRes.data.data || []);
        } catch (error) {
            console.error('Fetch error:', error);
            setVouchers([]);
            setMyVouchers([]);
            setMyUndian([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            refreshUser();
            fetchData();
        }, [])
    );

    const onRefresh = () => {
        setRefreshing(true);
        fetchData();
    };

    const formatNumber = (num) => {
        return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') || '0';
    };

    const formatCurrency = (num) => {
        return 'MG ' + formatNumber(num);
    };

    const handleClaimVoucher = async (voucher) => {
        Alert.alert(
            'Klaim Voucher',
            `Klaim voucher "${voucher.nama}" senilai ${formatCurrency(voucher.nilai)}?`,
            [
                { text: 'Batal', style: 'cancel' },
                { text: 'Klaim', onPress: () => claimVoucher(voucher.id) },
            ]
        );
    };

    const claimVoucher = async (voucherId) => {
        try {
            const response = await axios.post(API.VOUCHER.CLAIM, {
                member_id: user.id,
                voucher_id: voucherId,
            });

            if (response.data.success) {
                Alert.alert('🎉 Berhasil!', response.data.message);
                fetchData();
            } else {
                Alert.alert('Gagal', response.data.message);
            }
        } catch (error) {
            Alert.alert('Error', 'Gagal klaim voucher');
        }
    };

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={COLORS.primary} />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <StatusBar barStyle="dark-content" backgroundColor={COLORS.bgDarker} />

            {/* Header */}
            <View style={styles.header}>
                <Text style={styles.headerTitle}>Voucher</Text>
            </View>

            {/* Tabs */}
            <View style={styles.tabsContainer}>
                <TouchableOpacity
                    style={[styles.tab, activeTab === 'available' && styles.tabActive]}
                    onPress={() => setActiveTab('available')}
                >
                    <Text style={[styles.tabText, activeTab === 'available' && styles.tabTextActive]}>
                        Tersedia
                    </Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.tab, activeTab === 'my' && styles.tabActive]}
                    onPress={() => setActiveTab('my')}
                >
                    <Text style={[styles.tabText, activeTab === 'my' && styles.tabTextActive]}>
                        Voucher Saya ({myVouchers.length})
                    </Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.tab, activeTab === 'undian' && styles.tabActive]}
                    onPress={() => setActiveTab('undian')}
                >
                    <Text style={[styles.tabText, activeTab === 'undian' && styles.tabTextActive]}>
                        Undian ({myUndian.length})
                    </Text>
                </TouchableOpacity>
            </View>

            <ScrollView
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        tintColor={COLORS.primary}
                    />
                }
            >
                {activeTab === 'available' ? (
                    vouchers.length === 0 ? (
                        <View style={styles.emptyState}>
                            <Text style={styles.emptyIcon}>🎟️</Text>
                            <Text style={styles.emptyTitle}>Belum Ada Voucher</Text>
                            <Text style={styles.emptyText}>Voucher akan segera tersedia</Text>
                        </View>
                    ) : (
                        vouchers.map((voucher) => (
                            <View key={voucher.id} style={styles.voucherCard}>
                                <View style={styles.voucherLeft}>
                                    <View style={styles.voucherBadge}>
                                        <Text style={styles.voucherEmoji}>🎟️</Text>
                                    </View>
                                </View>
                                <View style={styles.voucherContent}>
                                    <Text style={styles.voucherName}>{voucher.nama}</Text>
                                    <Text style={styles.voucherValue}>{formatCurrency(voucher.nilai)}</Text>
                                    <Text style={styles.voucherDesc}>{voucher.deskripsi || 'Voucher diskon'}</Text>
                                </View>
                                <TouchableOpacity
                                    style={styles.claimBtn}
                                    onPress={() => handleClaimVoucher(voucher)}
                                >
                                    <Text style={styles.claimBtnText}>Klaim</Text>
                                </TouchableOpacity>
                            </View>
                        ))
                    )
                ) : activeTab === 'my' ? (
                    myVouchers.length === 0 ? (
                        <View style={styles.emptyState}>
                            <Text style={styles.emptyIcon}>📭</Text>
                            <Text style={styles.emptyTitle}>Belum Punya Voucher</Text>
                            <Text style={styles.emptyText}>Klaim voucher dari tab Tersedia</Text>
                        </View>
                    ) : (
                        myVouchers.map((voucher, index) => (
                            <View key={index} style={[styles.voucherCard, styles.myVoucherCard]}>
                                <View style={styles.voucherLeft}>
                                    <View style={[styles.voucherBadge, { backgroundColor: COLORS.success + '15' }]}>
                                        <Text style={styles.voucherEmoji}>✅</Text>
                                    </View>
                                </View>
                                <View style={styles.voucherContent}>
                                    <Text style={styles.voucherName}>{voucher.nama}</Text>
                                    <Text style={styles.voucherValue}>{formatCurrency(voucher.nilai)}</Text>
                                    <View style={styles.voucherCode}>
                                        <Text style={styles.voucherCodeText}>{voucher.kode}</Text>
                                    </View>
                                </View>
                                <View style={[styles.statusBadge,
                                voucher.status === 'active' ? styles.statusActive : styles.statusUsed
                                ]}>
                                    <Text style={[styles.statusText,
                                    { color: voucher.status === 'active' ? COLORS.success : COLORS.textMuted }
                                    ]}>
                                        {voucher.status === 'active' ? 'AKTIF' : 'DIPAKAI'}
                                    </Text>
                                </View>
                            </View>
                        ))
                    )
                ) : (
                    // Undian Tab
                    myUndian.length === 0 ? (
                        <View style={styles.emptyState}>
                            <Text style={styles.emptyIcon}>🎫</Text>
                            <Text style={styles.emptyTitle}>Belum Punya Undian</Text>
                            <Text style={styles.emptyText}>Tukar poin di menu Hadiah untuk mendapat undian</Text>
                        </View>
                    ) : (
                        <>
                            <View style={styles.undianHeader}>
                                <Text style={styles.undianHeaderText}>
                                    Total: {myUndian.length} lembar undian
                                </Text>
                            </View>
                            {myUndian.map((item, index) => (
                                <View key={index} style={styles.undianCard}>
                                    <View style={styles.undianTicketLeft}>
                                        <Text style={styles.undianTicketEmoji}>🎫</Text>
                                    </View>
                                    <View style={styles.undianTicketContent}>
                                        <Text style={styles.undianTicketNumber}>{item.nomor_undian}</Text>
                                        <Text style={styles.undianTicketPeriode}>{item.periode}</Text>
                                        <Text style={styles.undianTicketDate}>
                                            {new Date(item.created_at).toLocaleDateString('id-ID')}
                                        </Text>
                                    </View>
                                    <View style={[styles.undianStatus,
                                    item.status === 'winner' && styles.undianStatusWinner,
                                    item.status === 'expired' && styles.undianStatusExpired
                                    ]}>
                                        <Text style={[styles.undianStatusText,
                                        item.status === 'winner' && styles.undianStatusTextWinner
                                        ]}>
                                            {item.status === 'active' ? 'AKTIF' :
                                                item.status === 'winner' ? '🎉 MENANG' : 'EXPIRED'}
                                        </Text>
                                    </View>
                                </View>
                            ))}
                        </>
                    )
                )}

                <View style={{ height: 100 }} />
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.bgDark,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: COLORS.bgDark,
    },
    header: {
        paddingTop: Platform.OS === 'web' ? 20 : 50,
        paddingBottom: 16,
        paddingHorizontal: 20,
        backgroundColor: COLORS.bgDarker,
    },
    headerTitle: {
        fontSize: 22,
        fontWeight: '700',
        color: COLORS.textPrimary,
    },
    tabsContainer: {
        flexDirection: 'row',
        paddingHorizontal: 20,
        paddingVertical: 12,
        backgroundColor: COLORS.bgDarker,
        gap: 10,
    },
    tab: {
        flex: 1,
        paddingVertical: 10,
        alignItems: 'center',
        borderRadius: 10,
        backgroundColor: COLORS.bgDark,
    },
    tabActive: {
        backgroundColor: COLORS.primary,
    },
    tabText: {
        color: COLORS.textSecondary,
        fontWeight: '600',
        fontSize: 13,
    },
    tabTextActive: {
        color: '#fff',
    },
    scrollView: {
        flex: 1,
        paddingHorizontal: 10,
        paddingTop: 12,
    },
    voucherCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.bgCard,
        borderRadius: 14,
        padding: 16,
        marginBottom: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
        borderLeftWidth: 4,
        borderLeftColor: COLORS.info,
    },
    myVoucherCard: {
        borderLeftColor: COLORS.success,
    },
    voucherLeft: {
        marginRight: 14,
    },
    voucherBadge: {
        width: 50,
        height: 50,
        borderRadius: 12,
        backgroundColor: COLORS.info + '15',
        justifyContent: 'center',
        alignItems: 'center',
    },
    voucherEmoji: {
        fontSize: 24,
    },
    voucherContent: {
        flex: 1,
    },
    voucherName: {
        fontSize: 15,
        fontWeight: '600',
        color: COLORS.textPrimary,
        marginBottom: 4,
    },
    voucherValue: {
        fontSize: 18,
        fontWeight: '700',
        color: COLORS.success,
        marginBottom: 4,
    },
    voucherDesc: {
        fontSize: 12,
        color: COLORS.textSecondary,
    },
    voucherCode: {
        backgroundColor: COLORS.bgDark,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 6,
        alignSelf: 'flex-start',
    },
    voucherCodeText: {
        fontSize: 12,
        fontWeight: '700',
        color: COLORS.primary,
        letterSpacing: 1,
    },
    claimBtn: {
        backgroundColor: COLORS.primary,
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
    },
    claimBtnText: {
        color: '#fff',
        fontWeight: '600',
        fontSize: 13,
    },
    statusBadge: {
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 6,
    },
    statusActive: {
        backgroundColor: COLORS.success + '15',
    },
    statusUsed: {
        backgroundColor: COLORS.textMuted + '20',
    },
    statusText: {
        fontSize: 10,
        fontWeight: '700',
    },
    emptyState: {
        alignItems: 'center',
        padding: 50,
        backgroundColor: COLORS.bgCard,
        borderRadius: 16,
    },
    emptyIcon: {
        fontSize: 48,
        marginBottom: 12,
        opacity: 0.5,
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: COLORS.textPrimary,
        marginBottom: 4,
    },
    emptyText: {
        color: COLORS.textSecondary,
        fontSize: 13,
    },
    // Undian Styles
    undianHeader: {
        marginBottom: 12,
    },
    undianHeaderText: {
        fontSize: 13,
        color: COLORS.textSecondary,
        fontWeight: '600',
    },
    undianCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.bgCard,
        borderRadius: 14,
        padding: 14,
        marginBottom: 10,
        borderLeftWidth: 4,
        borderLeftColor: COLORS.accent,
    },
    undianTicketLeft: {
        width: 50,
        height: 50,
        borderRadius: 12,
        backgroundColor: COLORS.accent + '20',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    undianTicketEmoji: {
        fontSize: 24,
    },
    undianTicketContent: {
        flex: 1,
    },
    undianTicketNumber: {
        fontSize: 15,
        fontWeight: '700',
        color: COLORS.textPrimary,
        letterSpacing: 0.5,
    },
    undianTicketPeriode: {
        fontSize: 12,
        color: COLORS.primary,
        fontWeight: '500',
        marginTop: 2,
    },
    undianTicketDate: {
        fontSize: 11,
        color: COLORS.textMuted,
        marginTop: 2,
    },
    undianStatus: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 6,
        backgroundColor: COLORS.success + '15',
    },
    undianStatusWinner: {
        backgroundColor: COLORS.accent,
    },
    undianStatusExpired: {
        backgroundColor: COLORS.textMuted + '15',
    },
    undianStatusText: {
        fontSize: 10,
        fontWeight: '700',
        color: COLORS.success,
    },
    undianStatusTextWinner: {
        color: '#1a1a1a',
    },
});
